import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save, 
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  LogIn
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    industry: '',
    challenge: '',
    solution: '',
    results: [''],
    metrics: { roi: '', efficiency_gain: '', cost_reduction: '' },
    image_url: '',
    testimonial: '',
    testimonial_author: '',
    featured: false
  });

  const getAuthHeader = () => {
    return {
      auth: {
        username: credentials.username,
        password: credentials.password
      }
    };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      // Test authentication by fetching contacts (requires auth)
      await axios.get(`${API_URL}/contacts`, getAuthHeader());
      setIsAuthenticated(true);
      fetchCaseStudies();
    } catch (error) {
      setAuthError('Invalid credentials. Please try again.');
    }
  };

  const fetchCaseStudies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/case-studies`);
      setCaseStudies(response.data);
    } catch (error) {
      console.error('Error fetching case studies:', error);
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  };

  const seedCaseStudies = async () => {
    try {
      await axios.post(`${API_URL}/seed-case-studies`, {}, getAuthHeader());
      toast.success('Sample case studies created!');
      fetchCaseStudies();
    } catch (error) {
      if (error.response?.data?.status === 'skipped') {
        toast.info('Case studies already exist');
      } else {
        toast.error('Failed to seed case studies');
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMetricChange = (metric, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: { ...prev.metrics, [metric]: value }
    }));
  };

  const handleResultChange = (index, value) => {
    const newResults = [...formData.results];
    newResults[index] = value;
    setFormData(prev => ({ ...prev, results: newResults }));
  };

  const addResult = () => {
    setFormData(prev => ({ ...prev, results: [...prev.results, ''] }));
  };

  const removeResult = (index) => {
    setFormData(prev => ({
      ...prev,
      results: prev.results.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      client_name: '',
      industry: '',
      challenge: '',
      solution: '',
      results: [''],
      metrics: { roi: '', efficiency_gain: '', cost_reduction: '' },
      image_url: '',
      testimonial: '',
      testimonial_author: '',
      featured: false
    });
    setEditingId(null);
    setIsCreating(false);
  };

  const handleEdit = (caseStudy) => {
    setFormData({
      title: caseStudy.title,
      client_name: caseStudy.client_name,
      industry: caseStudy.industry,
      challenge: caseStudy.challenge,
      solution: caseStudy.solution,
      results: caseStudy.results || [''],
      metrics: caseStudy.metrics || { roi: '', efficiency_gain: '', cost_reduction: '' },
      image_url: caseStudy.image_url || '',
      testimonial: caseStudy.testimonial || '',
      testimonial_author: caseStudy.testimonial_author || '',
      featured: caseStudy.featured || false
    });
    setEditingId(caseStudy.id);
    setIsCreating(false);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        results: formData.results.filter(r => r.trim() !== '')
      };

      if (editingId) {
        await axios.put(`${API_URL}/case-studies/${editingId}`, payload, getAuthHeader());
        toast.success('Case study updated successfully!');
      } else {
        await axios.post(`${API_URL}/case-studies`, payload, getAuthHeader());
        toast.success('Case study created successfully!');
      }
      
      resetForm();
      fetchCaseStudies();
    } catch (error) {
      console.error('Error saving case study:', error);
      toast.error('Failed to save case study');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    
    try {
      await axios.delete(`${API_URL}/case-studies/${id}`, getAuthHeader());
      toast.success('Case study deleted successfully!');
      fetchCaseStudies();
    } catch (error) {
      console.error('Error deleting case study:', error);
      toast.error('Failed to delete case study');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="admin-login">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <h1 className="font-outfit text-2xl font-bold text-[#0A2540] mb-6 text-center">
            Admin Login
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
              <Input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter username"
                className="h-12"
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter password"
                className="h-12"
                data-testid="admin-password-input"
              />
            </div>
            {authError && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                {authError}
              </div>
            )}
            <Button
              type="submit"
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-12"
              data-testid="admin-login-button"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </form>
          <p className="text-sm text-slate-500 text-center mt-4">
            Default credentials: admin / blueribbon2024
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <div className="bg-[#0A2540] py-6">
        <div className="container-custom flex items-center justify-between">
          <h1 className="font-outfit text-2xl font-bold text-white">Case Studies Admin</h1>
          <div className="flex gap-3">
            <Button
              onClick={seedCaseStudies}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
              data-testid="seed-button"
            >
              Seed Sample Data
            </Button>
            <Button
              onClick={() => { setIsCreating(true); resetForm(); setIsCreating(true); }}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="create-case-study-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Case Study
            </Button>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {(isCreating || editingId) && (
          <div className="bg-white p-6 rounded-xl border border-slate-200 mb-8" data-testid="case-study-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-xl font-semibold text-[#0A2540]">
                {editingId ? 'Edit Case Study' : 'Create Case Study'}
              </h2>
              <Button variant="ghost" onClick={resetForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Case study title"
                  data-testid="form-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client Name *</label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => handleInputChange('client_name', e.target.value)}
                  placeholder="Client company name"
                  data-testid="form-client-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Industry *</label>
                <Input
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  placeholder="e.g., Retail, Manufacturing"
                  data-testid="form-industry"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Image URL</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => handleInputChange('image_url', e.target.value)}
                  placeholder="https://..."
                  data-testid="form-image-url"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Challenge *</label>
                <Textarea
                  value={formData.challenge}
                  onChange={(e) => handleInputChange('challenge', e.target.value)}
                  placeholder="Describe the client's challenge..."
                  data-testid="form-challenge"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Solution *</label>
                <Textarea
                  value={formData.solution}
                  onChange={(e) => handleInputChange('solution', e.target.value)}
                  placeholder="Describe your solution..."
                  data-testid="form-solution"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Results</label>
                {formData.results.map((result, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={result}
                      onChange={(e) => handleResultChange(index, e.target.value)}
                      placeholder={`Result ${index + 1}`}
                    />
                    {formData.results.length > 1 && (
                      <Button variant="ghost" onClick={() => removeResult(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" onClick={addResult} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" /> Add Result
                </Button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">ROI</label>
                <Input
                  value={formData.metrics.roi}
                  onChange={(e) => handleMetricChange('roi', e.target.value)}
                  placeholder="e.g., 340%"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Efficiency Gain</label>
                <Input
                  value={formData.metrics.efficiency_gain}
                  onChange={(e) => handleMetricChange('efficiency_gain', e.target.value)}
                  placeholder="e.g., 60%"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Testimonial</label>
                <Textarea
                  value={formData.testimonial}
                  onChange={(e) => handleInputChange('testimonial', e.target.value)}
                  placeholder="Client testimonial quote..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Testimonial Author</label>
                <Input
                  value={formData.testimonial_author}
                  onChange={(e) => handleInputChange('testimonial_author', e.target.value)}
                  placeholder="Name, Title"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => handleInputChange('featured', e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="featured" className="text-sm font-medium text-slate-700">
                  Featured Case Study
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200">
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button 
                onClick={handleSave}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                data-testid="save-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        )}

        {/* Case Studies List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200">
            <h2 className="font-semibold text-[#0A2540]">All Case Studies ({caseStudies.length})</h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#2563EB] mx-auto" />
            </div>
          ) : caseStudies.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              No case studies found. Create one or seed sample data.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {caseStudies.map((study) => (
                <div key={study.id} className="p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`case-study-row-${study.id}`}>
                  <div className="flex items-center gap-4">
                    {study.image_url && (
                      <img src={study.image_url} alt="" className="w-16 h-12 object-cover rounded" />
                    )}
                    <div>
                      <h3 className="font-medium text-[#0A2540]">{study.title}</h3>
                      <p className="text-sm text-slate-500">{study.client_name} • {study.industry}</p>
                    </div>
                    {study.featured && (
                      <span className="bg-[#2563EB]/10 text-[#2563EB] text-xs px-2 py-1 rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => handleEdit(study)} data-testid={`edit-${study.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" onClick={() => handleDelete(study.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-${study.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
